import React, { useEffect } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { InteractionStatus } from "@azure/msal-browser";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

const RequireAuth: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, inProgress, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      // If user just logged out, clear the marker and land them on home if desired
      const justLoggedOut = (() => {
        try {
          return localStorage.getItem("justLoggedOut");
        } catch {
          return null;
        }
      })();
      if (justLoggedOut === "1") {
        try {
          localStorage.removeItem("justLoggedOut");
        } catch {}
        navigate("/", { replace: true });
      }
    }
  }, [isAuthenticated, inProgress, navigate]);

  if (isAuthenticated) return <>{children}</>;

  // While an interaction is in progress, show a loader
  if (inProgress !== InteractionStatus.None) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated: show message and a button to go home ('/')
  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        p: 3,
        textAlign: "center",
      }}
    >
      <Typography variant="h5" component="h1">
        Not authenticated
      </Typography>
      <Typography variant="body1" sx={{ maxWidth: 520 }}>
        You need to sign in to view this page. Please go to the home page and
        click Login.
      </Typography>
      <Button
        variant="contained"
        color="primary"
        onClick={() => navigate("/", { replace: true })}
      >
        Go to Home
      </Button>
    </Box>
  );
};

export default RequireAuth;
